import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { VencimientoConCliente } from '@/types/vencimientos'
import { documentosService } from '@/core/services/documentosService'
import { diaCalendario } from '@/lib/diaCalendario'
import type { DatosPresentadoEmail } from './EnviarPresentadoEmailDialog'

type Presentacion = DatosPresentadoEmail['presentacion']
type Adjuntos = DatosPresentadoEmail['adjuntosSubidos']

/** Balance type from the sign of the determined amount: zero is no movement, negative is in favor. */
export const deducirSaldoTipo = (monto: number | null | undefined): Presentacion['saldoTipo'] => {
  if (monto == null) return undefined
  if (monto === 0) return 'sin_movimiento'
  return monto < 0 ? 'a_favor' : 'a_pagar'
}

/** Builds the data of the notification dialog for a vencimiento. The presentation date is a YYYY-MM-DD day. */
export const armarDatosPresentadoEmail = (
  vencimiento: VencimientoConCliente,
  presentacion: Presentacion,
  adjuntos: Adjuntos,
): DatosPresentadoEmail => ({
  vencimiento: {
    id: vencimiento.id ?? '',
    cliente_id: vencimiento.cliente_id ?? null,
    tipo_impuesto: vencimiento.tipo_impuesto ?? '',
    cliente_nombre: vencimiento.cliente_nombre ?? 'Cliente',
    cliente_email: vencimiento.cliente_email ?? '',
    periodo: vencimiento.periodo ?? '',
    fecha_vencimiento: vencimiento.fecha_vencimiento ?? '',
  },
  presentacion: { ...presentacion, fecha_presentacion: diaCalendario(presentacion.fecha_presentacion) ?? '' },
  adjuntosSubidos: adjuntos,
})

/** Presentation data stored in a presented vencimiento. The free message is not stored, so it stays empty. */
export const presentacionGuardada = (vencimiento: VencimientoConCliente): Presentacion => ({
  fecha_presentacion: vencimiento.fecha_presentacion ?? '',
  numero_presentacion: vencimiento.numero_presentacion ?? undefined,
  monto_determinado: vencimiento.monto_determinado ?? undefined,
  saldoTipo: deducirSaldoTipo(vencimiento.monto_determinado),
  forma_pago: (vencimiento.forma_pago ?? undefined) as Presentacion['forma_pago'],
  observaciones: vencimiento.observaciones ?? undefined,
})

/** Attachments of the dialog from the documents linked to a vencimiento. */
export const adjuntosDeDocumentos = (
  documentos: Array<{ id: string; nombre: string; archivo_path: string }>,
): Adjuntos => documentos.map((d) => ({ docId: d.id, nombre: d.nombre, path: d.archivo_path }))

/**
 * Links each document to the vencimiento as a receipt and returns the ids whose link failed.
 * The id has to be the one of the stored row that the mark RPC returns, not the id of the view row.
 * A failed link is logged and the rest go on.
 */
export const vincularAdjuntosPresentado = async (
  supabase: SupabaseClient<Database>,
  vencimientoId: string,
  docIds: string[],
): Promise<string[]> => {
  const fallidos: string[] = []
  for (const docId of docIds) {
    try {
      await documentosService.vincular(supabase, docId, 'vencimiento', vencimientoId, 'contable', 'comprobante')
    } catch (error) {
      console.error('Error al vincular adjunto:', error)
      fallidos.push(docId)
    }
  }
  return fallidos
}

/**
 * Links again the documents whose link failed and returns the ids that still fail.
 * Documents already linked to the vencimiento are skipped, so a link stored despite an error is not duplicated.
 * Throws if the current links cannot be read.
 */
export const reintentarVinculosPresentado = async (
  supabase: SupabaseClient<Database>,
  vencimientoId: string,
  docIds: string[],
): Promise<string[]> => {
  const vinculados = await documentosService.obtenerPorEntidad(supabase, 'vencimiento', vencimientoId, 'comprobante')
  const yaVinculados = new Set(vinculados.map((d) => d.id))
  return vincularAdjuntosPresentado(supabase, vencimientoId, docIds.filter((id) => !yaVinculados.has(id)))
}

/** Names of the attachments whose id is in the list, in the order of the attachments. */
export const nombresAdjuntos = (adjuntos: Adjuntos, docIds: string[]): string[] => {
  const ids = new Set(docIds)
  return adjuntos.filter((a) => ids.has(a.docId)).map((a) => a.nombre)
}
